import { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Radio, Skeleton } from 'antd';
import { twMerge } from '@utils/twMerge';
import { useLabelsHook } from './useLabels.hook';
import { LabelItem } from 'src/pages/QC/QCLabels/QCLabel';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';
import { useDXAReadingsSlice } from '@restore/DXAReadings/DXAReadings.slice';
import { useQCTReadingsSlice } from '@restore/QCTReadings/QCTReadings.slice';
import { selectAssignedQctLabelIds } from './qcLabels.helpers';
import { useParams } from 'src/hooks/useParams';

const activeButtonStyle = '!bg-waterLight !border-[#1D8EF2] !text-black';

export const QCLabels = () => {
  const { id: clinicalTrialId, taskId } = useParams();
  const { currentVisitLabels, open, isLoading } = useLabelsHook();

  const [dxaLabelIds, fetchDXAReadings] = useDXAReadingsSlice(
    ({ visitData }, { fetchDXAReadings }) => [
      visitData?.dxaScores
        ?.filter(dxa => dxa.labelId)
        .map(dxa => dxa.labelId || '') ?? [],
      fetchDXAReadings,
    ]
  );

  const [qctScores, qctLoading, fetchQCTReadings] = useQCTReadingsSlice(
    ({ visitData, loadingReadings }, { fetchQCTReadings }) => [
      visitData?.data,
      loadingReadings,
      fetchQCTReadings,
    ],
    // Every element is a stable reference, so a shallow compare stops this
    // panel re-rendering on unrelated store updates.
    shallowEqual
  );

  const selectedLabels = useMemo(
    () => [...dxaLabelIds, ...selectAssignedQctLabelIds(qctScores)],
    [dxaLabelIds, qctScores]
  );

  const fileTypes = useMemo(
    () => Object.keys(currentVisitLabels) as UploadFileTypeEnum[],
    [currentVisitLabels]
  );

  const [labelsFilter, setLabelsFilter] = useState<UploadFileTypeEnum | ''>('');

  useEffect(() => {
    const previousLocation = sessionStorage.getItem('previousLocation');

    const previousLocationPath = previousLocation?.length
      ? previousLocation.split('/')
      : '';
    const previousLocationTab =
      previousLocationPath[previousLocationPath.length - 1];

    setLabelsFilter(
      previousLocationTab?.length
        ? (previousLocationTab as UploadFileTypeEnum)
        : (fileTypes?.[0] ?? '')
    );
    if (
      fileTypes.includes(UploadFileTypeEnum.DXA) &&
      taskId &&
      clinicalTrialId
    ) {
      fetchDXAReadings({
        clinicalTrialId,
        taskId,
      });
    }

    if (
      fileTypes.includes(UploadFileTypeEnum.QCT) &&
      taskId &&
      clinicalTrialId
    ) {
      fetchQCTReadings({
        clinicalTrialId,
        taskId,
      });
    }
    // Slice actions are recreated every render and `fileTypes` is rebuilt by
    // the hook's groupBy, so listing them would refire this on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileTypes?.length, taskId, clinicalTrialId]);

  useEffect(() => {
    return () => {
      sessionStorage.setItem('previousLocation', '');
    };
  }, []);

  const init = open !== undefined;

  const isQctPending = (fileType: UploadFileTypeEnum | '') =>
    fileType === UploadFileTypeEnum.QCT && qctLoading;

  if (!init) {
    return <Skeleton active />;
  }

  return (
    <div>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <div className='w-[3px] h-[24px] bg-vividTangelo rounded-[2px]' />
          <h2 className='text-1824 font-medium'>Labels</h2>
        </div>
      </div>
      <Radio.Group
        className='flex justify-center mb-2'
        value={labelsFilter}
        buttonStyle='solid'
        onChange={e => {
          setLabelsFilter(e.target.value);
        }}
      >
        {fileTypes?.map(fileType => {
          return (
            <Radio.Button
              key={fileType}
              value={fileType}
              disabled={isQctPending(fileType)}
              className={twMerge(
                'w-[50%] text-center',
                labelsFilter === fileType && activeButtonStyle,
                fileTypes?.length < 2 && 'w-[100%]'
              )}
            >
              {fileType}
            </Radio.Button>
          );
        })}
      </Radio.Group>

      {isQctPending(labelsFilter) && <Skeleton active />}

      {!isQctPending(labelsFilter) &&
        !!currentVisitLabels[labelsFilter]?.length && (
          <div className='overflow-y-auto h-[calc(100vh-450px)]'>
            {currentVisitLabels[labelsFilter]?.map(label => {
              return (
                <LabelItem
                  key={label.id}
                  label={label}
                  selected={selectedLabels.includes(label.id)}
                />
              );
            })}
          </div>
        )}
      {!isLoading &&
        !isQctPending(labelsFilter) &&
        !currentVisitLabels[labelsFilter]?.length && (
          <div className='text-1624 text-dimGray text-center'>
            There aren't any labels for selected visit
          </div>
        )}
    </div>
  );
};
